/**
 * LiveCalendarBox — week grid with live-session time slots.
 *
 * <p>Parity port of the legacy akademi-lms-v2 {@code pages/live-calendar} page
 * which renders {@code <MyCalendar live viewType="week" />} backed by a
 * {@code searchCatalog} call filtered to Zoom / Teams / ClickMeeting pluginIds.
 * CSS-grid, no calendar library dependency, theme-agnostic (CSS custom
 * property tokens).
 *
 * <p>Renders a 5- or 7-column week grid (Mon–Fri or Mon–Sun depending on the
 * workWeek toggle). Each live course whose {@code startDate} falls inside the
 * window appears as a coloured event pill pinned to its column. The workHour
 * toggle restricts the visible pill list to sessions that start between 06:00
 * and 18:00 — matching the legacy CalendarToolbar slotMinTime/slotMaxTime.
 *
 * <p>The fetch goes through the portal BFF {@code POST /api/live-calendar}
 * which locks the plugin-id filter server-side; the block only sends the date
 * window and max-results cap. Anonymous visitors and network failures
 * soft-fail to the graceful empty state.
 *
 * <p>Navigation arrows advance/retreat the window by one cycle (7 or 5 days).
 * "Today" resets to the current week.
 *
 * [legacy ref: akademi-lms-v2 pages/live-calendar/index.js → MyCalendar
 *              live=true + components/calendar/CalendarToolbar.js
 *              workWeek/workHour]
 */
'use client';

import { useCallback, useEffect, useMemo, useReducer, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@akademi/site-builder-runtime';
import { SectionShell } from '../_shared/SectionShell';
import { type PaddingBox } from '../_shared/resolvePadding';
import { CalendarToolbar } from './CalendarToolbar';

export interface LiveCalendarBoxProps {
  title?: string;
  subtitle?: string;
  /** Design token size key e.g. "xl", "2xl". Maps to var(--font-size-{key}). */
  titleFontSize?: string;
  /** Max live sessions to request per week window. Default 100. */
  maxResults?: number;
  /** Where a session pill links to. Default `/lxp/courses`. */
  hrefBase?: string;
  /** Label for the "Today" reset button. */
  todayLabel?: string;
  /** Accessible label (icon button aria-label) for the previous-week nav button. */
  prevLabel?: string;
  /** Accessible label (icon button aria-label) for the next-week nav button. */
  nextLabel?: string;
  /** Empty-state message. */
  emptyLabel?: string;
  /**
   * Initial state of the Work-Week toggle (Mon–Fri only, hides Sat/Sun).
   * Mirrors legacy accountConfig.calendarWeekDaysOnly.
   */
  defaultWorkWeek?: boolean;
  /**
   * Initial state of the Work-Hour toggle (shows only sessions 06:00–18:00).
   * Mirrors legacy accountConfig.calendarBusinessHoursOnly / slotMinTime–slotMaxTime.
   */
  defaultWorkHour?: boolean;
  /** Toolbar label for the Work-Week toggle. */
  workWeekLabel?: string;
  /** Toolbar label for the Work-Hour toggle. */
  workHourLabel?: string;
  /** Weekday format in column headers — abbreviated ("Mon") or full
   *  ("Monday"). Default `short`. */
  dayFormat?: 'short' | 'long';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

/** Minimal catalog document shape (live course). */
interface LiveCourse {
  id: number | string;
  title?: string;
  startDate?: string | number;
  endDate?: string | number;
  pluginId?: string;
  thumbnail?: { url?: string } | null;
}

function apiUrl(path: string): string {
  const base = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/+$/, '');
  return `${base}${path}`;
}

function weekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

/** Local-calendar-date key (YYYY-MM-DD), NOT UTC — see CalendarBox.tsx's
 *  isoDate() for why `toISOString()` silently shifts dates by a day in
 *  positive-UTC-offset timezones. */
function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** e.g. "Mon 23" (or "Monday 23" when `dayFormat` is `long`) */
function colLabel(d: Date, locale: string, dayFormat: 'short' | 'long' = 'short'): string {
  return d.toLocaleDateString(locale, { weekday: dayFormat, day: 'numeric' });
}

/** e.g. "09:30" */
function timeFmt(ts: string | number | undefined, locale: string): string {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

function platformLabel(pluginId: string | undefined): string {
  if (!pluginId) return 'Live';
  if (pluginId.startsWith('Zoom')) return 'Zoom';
  if (pluginId === 'MsTeams') return 'Teams';
  if (pluginId === 'ClickMeeting') return 'ClickMeeting';
  return 'Live';
}

const PILL_COLORS: Record<string, string> = {
  ZoomMeetings: 'var(--color-info-600, #0284c7)',
  ZoomWebinar: 'var(--color-info-500, #0ea5e9)',
  MsTeams: 'var(--color-brand-600, #1f8ac0)',
  ClickMeeting: 'var(--color-success-600, #16a34a)',
};

function pillColor(pluginId: string | undefined): string {
  return pluginId
    ? (PILL_COLORS[pluginId] ?? 'var(--color-brand-500, #29a9e0)')
    : 'var(--color-brand-500, #29a9e0)';
}

const WORK_HOUR_START = 6; // 06:00
const WORK_HOUR_END = 18; // 18:00

function inWorkHours(ts: string | number | undefined): boolean {
  if (!ts) return false;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return false;
  const h = d.getHours();
  return h >= WORK_HOUR_START && h < WORK_HOUR_END;
}

type FetchState = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

/* Responsive style tag for LiveCalendarBox mobile grid. */
const LiveCalendarBoxStyles = (): null => {
  if (typeof document === 'undefined') return null;
  const id = 'ak-live-calendar-box-styles';
  if (!document.getElementById(id)) {
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      @media (max-width: 639px) {
        .ak-live-cal-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        .ak-live-cal-grid > div { min-block-size: 80px !important; }
      }
    `;
    document.head.appendChild(s);
  }
  return null;
};

interface State {
  weekOffset: number; // 0 = current week, ±n = n weeks from today
  courses: LiveCourse[];
  fetchState: FetchState;
  workWeek: boolean; // true = Mon–Fri only
  workHour: boolean; // true = filter to 06:00–18:00
}

type Action =
  | { type: 'NAV'; delta: number }
  | { type: 'RESET' }
  | { type: 'FETCH_START' }
  | { type: 'FETCH_OK'; courses: LiveCourse[] }
  | { type: 'FETCH_FAIL' }
  | { type: 'TOGGLE_WORK_WEEK' }
  | { type: 'TOGGLE_WORK_HOUR' };

function reducer(s: State, a: Action): State {
  switch (a.type) {
    case 'NAV':
      return { ...s, weekOffset: s.weekOffset + a.delta, fetchState: 'loading', courses: [] };
    case 'RESET':
      return { ...s, weekOffset: 0, fetchState: 'loading', courses: [] };
    case 'FETCH_START':
      return { ...s, fetchState: 'loading' };
    case 'FETCH_OK':
      return { ...s, courses: a.courses, fetchState: a.courses.length ? 'ready' : 'empty' };
    case 'FETCH_FAIL':
      return { ...s, courses: [], fetchState: 'error' };
    case 'TOGGLE_WORK_WEEK':
      return { ...s, workWeek: !s.workWeek };
    case 'TOGGLE_WORK_HOUR':
      return { ...s, workHour: !s.workHour };
    default:
      return s;
  }
}

function EventPill({
  c,
  hrefBase,
  locale,
}: {
  c: LiveCourse;
  hrefBase: string;
  locale: string;
}): ReactElement {
  const start = timeFmt(c.startDate, locale);
  const end = timeFmt(c.endDate, locale);
  const timeStr = start && end ? `${start}–${end}` : start || end;
  const platform = platformLabel(c.pluginId);
  const color = pillColor(c.pluginId);
  const href = `${hrefBase.replace(/\/$/, '')}/${c.id}`;

  return (
    <a
      href={href}
      style={{
        display: 'block',
        padding: '6px 8px',
        borderRadius: 'var(--radius-md, 8px)',
        background: color,
        color: 'var(--color-text-on-brand, #fff)',
        textDecoration: 'none',
        fontSize: 'var(--font-size-xs, 11.5px)',
        lineHeight: 1.35,
        marginBlockEnd: 4,
        overflow: 'hidden',
      }}
    >
      {timeStr && (
        <span style={{ display: 'block', fontWeight: 700, marginBlockEnd: 1 }}>{timeStr}</span>
      )}
      <span
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          fontWeight: 600,
        }}
      >
        {c.title || platform}
      </span>
      <span
        style={{
          display: 'block',
          marginBlockStart: 2,
          fontSize: 'var(--font-size-xs, 10px)',
          opacity: 0.8,
          fontWeight: 500,
          letterSpacing: 'var(--font-tracking-wider, 0.04em)',
          textTransform: 'uppercase',
        }}
      >
        {platform}
      </span>
    </a>
  );
}

function GridSkeleton({ cols = 7 }: { cols?: number }): ReactElement {
  return (
    <div
      className="ak-live-cal-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: 'var(--space-2, 8px)',
        paddingBlockStart: 8,
      }}
    >
      {Array.from({ length: cols }).map((_, i) => (
        <div
          key={i}
          aria-hidden
          style={{
            blockSize: 120,
            borderRadius: 'var(--radius-lg, 14px)',
            background: 'var(--color-border-subtle, rgba(0,0,0,0.06))',
            animation: 'ak-pulse 1.4s ease-in-out infinite',
            animationDelay: `${i * 80}ms`,
          }}
        />
      ))}
    </div>
  );
}

export function LiveCalendarBox({
  title,
  subtitle,
  titleFontSize,
  maxResults = 100,
  hrefBase = '/lxp/courses',
  todayLabel,
  prevLabel,
  nextLabel,
  emptyLabel,
  defaultWorkWeek = false,
  defaultWorkHour = false,
  workWeekLabel,
  workHourLabel,
  dayFormat = 'short',
  paddingOuter,
  paddingInner,
}: LiveCalendarBoxProps): ReactElement {
  const { t } = useTranslation('common');
  const { locale } = useLocale();

  // Same locale-binding fix as CalendarBox: these were hardcoded English
  // literal prop defaults, so switching site language never translated
  // the toolbar chrome.
  const resolvedTitle = title ?? t('label.liveCalendar');
  const resolvedTodayLabel = todayLabel ?? t('label.today');
  const resolvedPrevLabel = prevLabel ?? t('label.previous');
  const resolvedNextLabel = nextLabel ?? t('label.next');
  const resolvedWorkWeekLabel = workWeekLabel ?? t('label.workWeek');
  const resolvedWorkHourLabel = workHourLabel ?? t('label.workHour');
  const resolvedEmptyLabel = emptyLabel ?? t('message.noLiveSessionsThisWeek');
  const [state, dispatch] = useReducer(reducer, {
    weekOffset: 0,
    courses: [],
    fetchState: 'loading',
    workWeek: defaultWorkWeek,
    workHour: defaultWorkHour,
  });

  const dayCount = state.workWeek ? 5 : 7;

  const mon = useMemo(() => {
    const base = weekStart(new Date());
    return addDays(base, state.weekOffset * 7);
  }, [state.weekOffset]);

  const windowEnd = useMemo(() => addDays(mon, dayCount - 1), [mon, dayCount]);

  const days = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => addDays(mon, i)),
    [mon, dayCount],
  );

  const windowLabel = useMemo(() => {
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const l = mon.toLocaleDateString(locale, opts);
    const r = windowEnd.toLocaleDateString(locale, opts);
    const yr = windowEnd.getFullYear();
    return `${l} – ${r}, ${yr}`;
  }, [mon, windowEnd, locale]);

  // Always fetch the full Mon–Sun window regardless of the workWeek toggle so
  // toggling workWeek is instant (no re-fetch needed).
  const fullSun = useMemo(() => addDays(mon, 6), [mon]);

  const byDay = useMemo(() => {
    const map = new Map<string, LiveCourse[]>();
    for (const c of state.courses) {
      if (!c.startDate) continue;
      if (state.workHour && !inWorkHours(c.startDate)) continue;
      const d = new Date(c.startDate);
      if (Number.isNaN(d.getTime())) continue;
      const key = isoDate(d);
      const bucket = map.get(key) ?? [];
      bucket.push(c);
      map.set(key, bucket);
    }
    for (const bucket of map.values()) {
      bucket.sort((a, b) => {
        const at = a.startDate ? new Date(a.startDate).getTime() : 0;
        const bt = b.startDate ? new Date(b.startDate).getTime() : 0;
        return at - bt;
      });
    }
    return map;
  }, [state.courses, state.workHour]);

  const doFetch = useCallback(
    (startDate: string, endDate: string, signal: AbortSignal) => {
      dispatch({ type: 'FETCH_START' });
      fetch(apiUrl('/api/live-calendar'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ startDate, endDate, maxResults }),
        signal,
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
        .then((data: { documents?: LiveCourse[] }) => {
          if (signal.aborted) return;
          const docs = Array.isArray(data?.documents) ? data.documents : [];
          dispatch({ type: 'FETCH_OK', courses: docs });
        })
        .catch(() => {
          if (!signal.aborted) dispatch({ type: 'FETCH_FAIL' });
        });
    },
    [maxResults],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    const start = mon.toISOString();
    const endExcl = addDays(fullSun, 1);
    endExcl.setHours(0, 0, 0, 0);
    const end = endExcl.toISOString();
    doFetch(start, end, ctrl.signal);
    return () => ctrl.abort();
  }, [doFetch, mon, fullSun]);

  const todayKey = isoDate(new Date());

  const visibleEmpty =
    state.fetchState !== 'loading' && days.every((d) => !byDay.get(isoDate(d))?.length);

  return (
    <SectionShell
      title={resolvedTitle}
      subtitle={subtitle}
      titleFontSize={titleFontSize}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      <LiveCalendarBoxStyles />
      <CalendarToolbar
        title={windowLabel}
        onPrev={() => dispatch({ type: 'NAV', delta: -1 })}
        onNext={() => dispatch({ type: 'NAV', delta: 1 })}
        onToday={() => dispatch({ type: 'RESET' })}
        todayLabel={resolvedTodayLabel}
        prevLabel={resolvedPrevLabel}
        nextLabel={resolvedNextLabel}
        workWeek={state.workWeek}
        workHour={state.workHour}
        onToggleWorkWeek={() => dispatch({ type: 'TOGGLE_WORK_WEEK' })}
        onToggleWorkHour={() => dispatch({ type: 'TOGGLE_WORK_HOUR' })}
        workWeekLabel={resolvedWorkWeekLabel}
        workHourLabel={resolvedWorkHourLabel}
      />

      {state.fetchState === 'loading' ? (
        <GridSkeleton cols={dayCount} />
      ) : (
        <>
          <WeekGrid
            days={days}
            todayKey={todayKey}
            byDay={byDay}
            hrefBase={hrefBase}
            workHour={state.workHour}
            locale={locale}
            dayFormat={dayFormat}
          />
          {state.fetchState === 'error' && (
            <div
              role="alert"
              style={{
                marginBlockStart: 12,
                padding: '14px 20px',
                textAlign: 'center',
                borderRadius: 'var(--radius-lg, 14px)',
                border: '1px solid var(--color-danger-200, #fecaca)',
                background: 'var(--color-danger-50, #fff5f5)',
                color: 'var(--color-danger-700, #b91c1c)',
                fontSize: 'var(--font-size-sm, 14px)',
              }}
            >
              {t('message.liveSessionsLoadFailed') ||
                'Failed to load sessions. Please refresh to try again.'}
            </div>
          )}
          {state.fetchState !== 'error' && (state.fetchState === 'empty' || visibleEmpty) && (
            <div
              role="status"
              aria-live="polite"
              style={{
                marginBlockStart: 12,
                padding: '16px 24px',
                textAlign: 'center',
                borderRadius: 'var(--radius-lg, 14px)',
                border: '1px dashed var(--color-border-strong, rgba(0,0,0,0.18))',
                color: 'var(--color-text-secondary, #52525b)',
                fontSize: 'var(--font-size-sm, 14px)',
              }}
            >
              {resolvedEmptyLabel}
            </div>
          )}
        </>
      )}
    </SectionShell>
  );
}

function WeekGrid({
  days,
  todayKey,
  byDay,
  hrefBase,
  workHour,
  locale,
  dayFormat,
}: {
  days: Date[];
  todayKey: string;
  byDay: Map<string, LiveCourse[]>;
  hrefBase: string;
  workHour: boolean;
  locale: string;
  dayFormat: 'short' | 'long';
}): ReactElement {
  const cols = days.length;
  return (
    <div
      className="ak-live-cal-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: 'var(--space-2, 8px)',
      }}
    >
      {days.map((day) => {
        const key = isoDate(day);
        const isToday = key === todayKey;
        const events = byDay.get(key) ?? [];
        return (
          <div
            key={key}
            style={{
              minBlockSize: 120,
              borderRadius: 'var(--radius-lg, 14px)',
              border: isToday
                ? '2px solid var(--color-brand-500, #29a9e0)'
                : '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
              background: isToday
                ? 'var(--color-brand-50, #e9f7fc)'
                : 'var(--color-surface-raised, #fff)',
              padding: '8px 6px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-xs, 11px)',
                fontWeight: isToday ? 800 : 700,
                textTransform: 'uppercase',
                letterSpacing: 'var(--font-tracking-wider, 0.05em)',
                color: isToday
                  ? 'var(--color-brand-700, #166a98)'
                  : 'var(--color-text-secondary, #52525b)',
                marginBlockEnd: 8,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {colLabel(day, locale, dayFormat)}
            </div>

            {workHour && (
              <div
                aria-label="06:00-18:00"
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: 'var(--font-tracking-wider, 0.06em)',
                  textTransform: 'uppercase',
                  color: 'var(--color-text-tertiary, #a1a1aa)',
                  marginBlockEnd: 6,
                  paddingBlockEnd: 4,
                  borderBlockEnd: '1px dashed var(--color-border-subtle, rgba(0,0,0,0.08))',
                }}
              >
                06:00–18:00
              </div>
            )}

            <div style={{ flex: 1, minBlockSize: 0 }}>
              {events.map((c) => (
                <EventPill
                  key={`${c.id}-${c.startDate}`}
                  c={c}
                  hrefBase={hrefBase}
                  locale={locale}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
